import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Car, Hammer, Wallet, FileText, LogOut, Menu, X, Sun, Moon, Truck, Users, History } from "lucide-react";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/", label: "Genel Bakış", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/araclar", label: "Otomotiv", icon: Car, testId: "nav-vehicles" },
  { to: "/projeler", label: "İnşaat", icon: Hammer, testId: "nav-projects" },
  { to: "/hafriyat", label: "Hafriyat", icon: Truck, testId: "nav-excavation" },
  { to: "/finans", label: "Finans & Ödemeler", icon: Wallet, testId: "nav-finance" },
  { to: "/tanimlar", label: "Cari & Personel", icon: Users, testId: "nav-parties" },
  { to: "/raporlar", label: "Raporlar", icon: FileText, testId: "nav-reports" },
  { to: "/islem-gecmisi", label: "İşlem Geçmişi", icon: History, testId: "nav-audit" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = localStorage.getItem("theme");
    if (stored) return stored === "dark";
    return document.documentElement.classList.contains("dark");
  });

  const toggleTheme = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/giris");
  };

  return (
    <div className="min-h-screen bg-background flex">
      {open && <div className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm md:hidden" onClick={() => setOpen(false)} data-testid="sidebar-backdrop" />}
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className={`fixed md:static inset-y-0 left-0 z-40 w-64 bg-card border-r border-border transform transition-transform duration-200 ${open ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
      >
        <div className="h-16 flex items-center px-6 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-bold text-sm">İY</div>
            <div>
              <div className="font-display font-bold text-sm leading-tight">İş Yönetim</div>
              <div className="text-[11px] text-muted-foreground">Otomotiv · İnşaat · Hafriyat</div>
            </div>
          </div>
        </div>
        <nav className="p-3 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testId}
              onClick={() => setOpen(false)}
              className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3 px-1">
            <div className="w-9 h-9 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold text-sm">
              {(user?.name || "U")[0]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="user-name">{user?.name || "Kullanıcı"}</div>
              <div className="text-xs text-muted-foreground truncate" data-testid="user-role">{user?.role_name} · {user?.company?.name}</div>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full justify-start gap-2" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4" /> Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-border bg-card/80 backdrop-blur sticky top-0 z-30 px-4 md:px-8 flex items-center justify-between">
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="hidden md:block">
            <div className="text-xs text-muted-foreground uppercase tracking-widest font-medium">Yönetim Paneli</div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 overflow-x-hidden">{children}</main>
      </div>
    </div>
  );
}
